import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { ActionIcon, Switch, Tooltip } from "rizzui";
import { toast } from "sonner";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { MdOutlineModeEditOutline } from "react-icons/md";
import DeletePopover from "../../../components/buttons/DeletePopover";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { FormDrawerView } from "../../../components/forms/FormDrawer"; 
import CategoriesFilters from "./CategoriesFilters";
import CategoryForm from "../components/CategoryForm";
import { CategoryService } from "../../../services/category/categoryService";
import { CategoriesApiResponse, Category } from "../../../type/entities/category-type";

interface CategoryListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    categorySelected: Category | null;
    setCategorySelected: React.Dispatch<React.SetStateAction<Category | null>>;
}

const columnOrder = [
    'id',
    'company',
    'color',
    'name',
    'description',
    'active',
    'createdAt',
    'updatedAt'
];

const CategoryList: FC<CategoryListProps> = ({ openFormDrawer, setOpenFormDrawer, categorySelected, setCategorySelected }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new CategoryService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE CATEGORÍAS
     * @EN GETS THE LIST OF CATEGORIES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listCategories(filters);
        return response.getResponseData() as CategoriesApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DE LA CATEGORÍA
     * @EN TOGGLE CATEGORY STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleCategoryStatus(id, status)).getResponseData(); 
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(response.message);
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UNA CATEGORÍA
     * @EN DELETES A CATEGORY
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = (await service.deleteCategory(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success("Categoría eliminada correctamente");
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA VARIAS CATEGORÍAS
     * @EN DELETES MULTIPLE CATEGORIES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = (await service.deleteMultiCategory(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success("Categorías eliminadas correctamente");
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'categories-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['color', 'active']}
                    columnsNotShown={[
                        'color',
                    ]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'name',
                            columnNameKey: 'Nombre',
                            render: (row: any) => {
                                return (
                                    <div className='flex items-center'>
                                        <div className={`w-4 h-4 rounded inline-block mr-2`} style={{ backgroundColor: row.row.original?.color }}></div>
                                        {row.row.original?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'description',
                            columnNameKey: 'Descripción',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.description || 'N/A'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.active ? 'Desactivar' : 'Activar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.active}
                                            disabled={changingStatus.includes(row.row.original.id)}
                                            onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'category', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setCategorySelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'category', permission: 'delete' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Eliminar" placement="top" color="invert">
                                    <div>
                                        <DeletePopover
                                            title="Eliminar"
                                            description="¿Estás seguro de que deseas eliminar esta categoría?"
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            ),
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as CategoriesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Categorías'} onClear={() => resetFilters(50)}>
                <CategoriesFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    categorySelected ?
                        "Completa el formulario para editar los datos de la categoría" :
                        "Completa el formulario para crear una nueva categoría"
                }
                drawerTitle={categorySelected ? 'Editar Categoría' : 'Crear Categoría'} submitText={categorySelected ? 'Guardar Categoría' : 'Crear Categoría'}>
                <CategoryForm setOpenModal={setOpenFormDrawer} category={categorySelected} refetch={refetch} />
            </FormDrawerView>

        </Page>
    );
};

export default CategoryList;